import { Box, Button, Collapse, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MdCheck, MdRadioButtonUnchecked } from 'react-icons/md'
import { useOptionalIperiaStatus } from '../../hooks/useIperiaStatus'
import { frenchFeminineOrdinal } from '../../utils/frenchOrdinal'

/**
 * Situe l'apprenante dans son lot quand plusieurs formations lui ont été vendues d'un coup.
 *
 * Elle reçoit un lien par dossier, à quelques jours d'intervalle, et déroule à chaque fois le même
 * parcours. Sans ce repère, deux dossiers du lot sont indiscernables l'un de l'autre : elle croit
 * rejouer par erreur un parcours déjà fait, ou abandonne parce qu'on lui redemande la même chose.
 *
 * Ne rend rien pour un dossier isolé, ni en dehors du parcours de l'apprenante.
 */
const IperiaLotBanner = () => {
  const theme = useTheme()
  const location = useLocation()
  const group = useOptionalIperiaStatus()?.status?.group
  const onSuccess = location.pathname.endsWith('/success')
  // Sur téléphone, la liste complète repoussait le formulaire sous la ligne de flottaison à chaque étape.
  const compact = useMediaQuery(theme.breakpoints.down('sm'))
  const [expanded, setExpanded] = useState(false)

  if (!group) return null

  return (
    <Box
      role="status"
      sx={{
        background: theme.palette.primary.light,
        borderRadius: 1,
        px: 2,
        py: 1.5,
        mb: 3,
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
        {frenchFeminineOrdinal(group.position)} formation sur {group.size}
      </Typography>
      {!onSuccess && (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
          {group.position > 1
            ? 'Vos infos et documents sont déjà enregistrés. Seul le bulletin se signe à chaque formation.'
            : `Vos infos et documents ne sont demandés qu'une fois pour les ${group.size} formations. Seul le bulletin se signe à chaque formation.`}
        </Typography>
      )}
      {compact && (
        <Button
          size="small"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          sx={{ mt: 0.5, px: 0, textTransform: 'none', color: 'primary.dark', fontWeight: 600 }}
        >
          {expanded ? 'Masquer les formations' : `Voir les ${group.size} formations`}
        </Button>
      )}
      <Collapse in={!compact || expanded}>
      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        {group.registrations.map((registration) => (
          <Stack key={registration.global_id} direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ display: 'flex', mt: '2px' }}>
              {registration.learner_signed ? (
                <MdCheck
                  size={16}
                  color={theme.palette.success.main}
                  aria-label="Formation signée"
                  title="Formation signée"
                />
              ) : (
                <MdRadioButtonUnchecked
                  size={16}
                  color={theme.palette.text.disabled}
                  aria-label="Formation à signer"
                  title="Formation à signer"
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: registration.is_current ? 600 : 400,
                color: registration.is_current ? theme.palette.text.primary : theme.palette.text.secondary,
              }}
            >
              {registration.product_name}
            </Typography>
          </Stack>
        ))}
      </Stack>
      </Collapse>
    </Box>
  )
}

export default IperiaLotBanner
